import { CSSProperties, useState } from 'react';
import Banner from 'elements/Banner';
import ButtonSymphony from 'elements/ButtonSymphony';
import Icon from 'elements/Icon';
import Colors from 'modules/Colors';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import usePlans from 'Hooks/usePlans';
import SetupPaymentMethod from './SetupPaymentMethod';
import ModalSymphony from 'elements/ModalSymphony';
import { PlanType } from 'modules/Const';
import { Body2 } from './Typography';

interface TeamTrialBannerProps {
    className?: string;
    style?: CSSProperties;
}

const TeamTrialBanner = ({
    className = 'teamTrialBanner',
    style
}: TeamTrialBannerProps) => {
    const { organization } = useOrganizationV2();
    const { getMonthlyPrice } = usePlans();
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    // Don't show banner if no organization or trial has ended
    if (!organization?.trial_end_date) {
        return null;
    }

    const trialEndDate = new Date(organization.trial_end_date);
    const now = new Date();
    const daysRemaining = Math.max(0, Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    const hasTrialExpired = daysRemaining <= 0;

    // Don't show if trial has been active for a while (assume payment method added)
    if (!hasTrialExpired && daysRemaining > 7) {
        return null;
    }

    const handleAddPaymentMethod = () => {
        setShowPaymentModal(true);
    };

    const handlePaymentSuccess = () => {
        setShowPaymentModal(false);
        // Refresh organization data to update trial status
        window.location.reload();
    };

    const handlePaymentError = (error: string) => {
        console.error('Payment error:', error);
        // Keep modal open for retry
    };

    const renderTrialBanner = () => {
        if (hasTrialExpired) {
            return (
                <Banner
                    className={className}
                    color="red"
                    style={style}
                    iconLeft={<Icon className='warning' icon="warning" size={24} color={Colors.red} />}
                    title="Your team trial has ended!"
                >
                    <div style={sty.bannerContent}>
                        <Body2 style={sty.description}>
                            Please add a payment method to continue using your team. You currently have{' '}
                            {organization?.brands?.length || 0} brands on your team.
                        </Body2>
                        
                        <div style={sty.actionButtons}>
                            <ButtonSymphony
                                className="addPaymentMethodButton"
                                onClick={handleAddPaymentMethod}
                                variant="primary"
                                color="red"
                                style={sty.primaryButton}
                            >
                                Add Payment Method
                            </ButtonSymphony>
                        </div>
                    </div>
                </Banner>
            );
        }

        return (
            <Banner
                className={className}
                color="yellow"
                style={style}
                iconLeft={<Icon className='clock' icon="clock" size={24} color={Colors.yellow} />}
                title={`You have ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left on your team trial`}
            >
                <div style={sty.bannerContent}>
                    <Body2 style={sty.description}>
                        Add a payment method now to ensure uninterrupted access to your team features.
                    </Body2>
                    
                    <div style={sty.actionButtons}>
                        <ButtonSymphony
                            className="addPaymentMethodButton"
                            onClick={handleAddPaymentMethod}
                            variant="primary"
                            color="purple"
                            style={sty.primaryButton}
                        >
                            Add Payment Method
                        </ButtonSymphony>
                    </div>
                </div>
            </Banner>
        );
    };

    const renderPaymentModal = () => {
        if (!showPaymentModal || !organization) {
            return null;
        }

        return (
            <ModalSymphony
                className="teamTrialPaymentModal"
                isOpen={showPaymentModal}
                onClose={() => setShowPaymentModal(false)}
                title="Add Payment Method"
                subtitle="Secure your team subscription with a payment method"
                size="medium"
            >
                <div style={sty.modalContent}>
                    <Body2 style={sty.modalDescription}>
                        Adding a payment method will convert your trial to an active Team subscription.
                        You'll be charged ${getMonthlyPrice(PlanType.TEAM_V2) || 199}/month for unlimited access to team features.
                    </Body2>

                    <SetupPaymentMethod
                        organizationId={organization?.id || 0}
                        onSuccess={handlePaymentSuccess}
                        onError={handlePaymentError}
                        onCancel={() => setShowPaymentModal(false)}
                        submitButtonText="Add Payment Method"
                        showCancelButton={true}
                    />
                </div>
            </ModalSymphony>
        );
    };

    return (
        <>
            {renderTrialBanner()}
            {renderPaymentModal()}
        </>
    );
};

const sty: Record<string, CSSProperties> = {
    bannerContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        marginTop: 8
    },
    description: {
        color: Colors.greyDark,
        lineHeight: '1.4'
    },
    actionButtons: {
        display: 'flex',
        gap: 12,
        alignItems: 'center'
    },
    primaryButton: {
        minWidth: 180
    },
    modalContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24
    },
    modalDescription: {
        color: Colors.greyDark,
        textAlign: 'center'
    }
};

export default TeamTrialBanner;
